import React, { useContext, useEffect, useState } from 'react';
import {
  Box, Button, FormControl, FormLabel, Input,
  FormErrorMessage, VStack, useToast, Flex, Select,
  InputGroup, InputRightElement, IconButton, Text
} from '@chakra-ui/react';
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import CommonService from '../services/CommonService';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import WebLayout from '../layouts/WebLayout';

const validationSchema = Yup.object().shape({
  FName: Yup.string().required('First Name is required'),
  LName: Yup.string().required('Last Name is required'),
  MobileNo: Yup.string()
    .matches(/^\d{10}$/, 'Mobile Number must be exactly 10 digits')
    .required('Mobile Number is required'),
  Email: Yup.string().email('Invalid email format').required('Email is required'),
  Password: Yup.string()
    .min(6, 'Password must be at least 6 characters long')
    .required('Password is required'),
  CompCd: Yup.string().required('Company is required'),
  EmpId: Yup.string().when('CompCd', (CompCd, schema) => {
    const code = parseInt(CompCd[0], 10);
    if (code > 1) {
      return schema.required('Employee ID is required for selected company');
    }
    return schema.notRequired();
  }),
});

const Signup = () => {
  const { signupUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const toast = useToast();

  const [company, setCompany] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true); // Loading state for company list
  const [isSigningUp, setIsSigningUp] = useState(false); // Loading state for signup button
  const [showPassword, setShowPassword] = useState(false); // State for password visibility

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { // Set default value for CompCd to ensure 'Select' is chosen initially
      CompCd: '',
    }
  });

  const selectedCompany = watch("CompCd"); // watch the company selection

  const companyList = async () => {
    setCompany([]);
    setIsLoadingCompanies(true);
    try {
      const res = await CommonService.getCompany();
      if (res.statusCode === 200) {
        setCompany(res.data);
      } else {
        toast({
          title: 'Failed to load companies',
          description: res.message || 'An unexpected error occurred.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right'
        });
      }
    } catch (error) {
      toast({
        title: 'Error fetching companies',
        description: error.response?.data?.message || 'Something went wrong. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right'
      });
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  useEffect(() => {
    companyList();
  }, []);

  const onSubmit = async (formData) => {
    setIsSigningUp(true);
    try {
      await signupUser(formData);
      toast({
        title: 'Signup Successful',
        description: 'You can now log in with your new account.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right'
      });

      reset();
      navigate("/login");

    } catch (err) {
      toast({
        title: 'Signup failed',
        description: err.response?.data?.message || 'Something went wrong. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right'
      });
    } finally {
      setIsSigningUp(false);
    }
  };

  return (
    <WebLayout>
      <Flex minH="100vh" align="center" justify="center" bg="gray.50" >
        <Box
          bg="white"
          p={6}
          rounded="md"
          shadow="2xl"
          w="full"
          maxW="sm"
          mx={{ base: 4, md: 0 }}
          border="1px"
          borderColor="gray.200"
          textAlign="center"
          position="relative"
        >
          <VStack spacing={2} as="form" onSubmit={handleSubmit(onSubmit)}>

            <Flex gap={2} direction={{ base: 'column', sm: 'row' }} w="full">
              <FormControl isInvalid={!!errors.FName} flex="1">
                <FormLabel fontSize="sm">First Name</FormLabel>
                <Input
                  type="text"
                  {...register("FName")}
                  size='sm'
                />
                <FormErrorMessage>{errors.FName?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.LName} flex="1">
                <FormLabel fontSize="sm">Last Name</FormLabel>
                <Input
                  type="text"
                  {...register("LName")}
                  size='sm'
                />
                <FormErrorMessage>{errors.LName?.message}</FormErrorMessage>
              </FormControl>
            </Flex>

            {/* Email */}
            <FormControl isInvalid={!!errors.Email} w="full">
              <FormLabel fontSize="sm">Email</FormLabel>
              <Input
                type="email"
                inputMode="email"
                {...register("Email")}
                size='sm'
              />
              <FormErrorMessage>{errors.Email?.message}</FormErrorMessage>
            </FormControl>

            <Flex gap={2} direction={{ base: 'column', sm: 'row' }} w="full">
              <FormControl isInvalid={!!errors.MobileNo} flex="1">
                <FormLabel fontSize="sm">Mobile</FormLabel>
                <Input
                  type="number"
                  inputMode="numeric"
                  {...register("MobileNo")}
                  size='sm'
                />
                <FormErrorMessage>{errors.MobileNo?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.Password} flex="1">
                <FormLabel fontSize="sm">Password</FormLabel>
                <InputGroup size='sm'>
                  <Input
                    type={showPassword ? "text" : "password"}
                    {...register("Password")}
                  />
                  <InputRightElement width='2.5rem'>
                    <IconButton
                      h='1.75rem'
                      size='sm'
                      onClick={() => setShowPassword(!showPassword)}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      variant="ghost"
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors.Password?.message}</FormErrorMessage>
              </FormControl>
            </Flex>

            {/* Company and Employee ID */}
            <Flex gap={4} direction={{ base: 'column', sm: 'row' }} w="full">
              <FormControl isInvalid={!!errors.CompCd} flex="1">
                <FormLabel fontSize="sm">Company</FormLabel>
                <Select
                  size="sm"
                  {...register("CompCd")}
                  isDisabled={isLoadingCompanies}
                >
                  {isLoadingCompanies ? (
                    <option value="">Loading Companies...</option>
                  ) : (
                    <>
                      <option value="">Select Company</option>
                      {company.map((row) => (
                        <option value={row.CompCd} key={row.CompCd}>{row.Name}</option>
                      ))}
                    </>
                  )}
                </Select>
                <FormErrorMessage>{errors.CompCd?.message}</FormErrorMessage>
              </FormControl>


              {parseInt(selectedCompany, 10) > 1 && (
                <FormControl isInvalid={!!errors.EmpId} flex="1">
                  <FormLabel fontSize="sm">Employee ID</FormLabel>
                  <Input
                    type="text"
                    {...register("EmpId")}
                    size='sm'
                  />
                  <FormErrorMessage>{errors.EmpId?.message}</FormErrorMessage>
                </FormControl>
              )}
            </Flex>

            {/* Sign Up Button */}
            <Button
              colorScheme="teal"
              type="submit"
              width="full"
              size='sm'
              isLoading={isSigningUp} // Show loading spinner
              loadingText="Signing Up..."
            >
              Sign Up
            </Button>
          </VStack>

          {/* Login Link */}
          <Box mt={4} textAlign="center">
            <Text fontSize="sm">
              Already have an account?{' '}
              <RouterLink to="/login">
                <Text as="span" color="teal.500" fontWeight="semibold">
                  Login
                </Text>
              </RouterLink>
            </Text>
          </Box>
        </Box>
      </Flex>
    </WebLayout>
  );
};

export default Signup;